import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useFormContext, Controller, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useGlobalSlice } from "app/slice";
import {
  buildPreloadedSimDeviceFingerprint,
  clearPreloadedSimChoosePlanAndSecurityInSession,
  hasPreloadedSimIncludedPlan,
  maybeResetPreloadedSimAfterCommittedDeviceEdited,
} from "../utils/preloaded-sim-activation-session-cascade";
import {
  getPreloadedSimCheckoutNavigateState,
  preparePreloadedSimSkipAllUpsellsForCheckout,
  resolvePreloadedSimUpsellStepSkips,
} from "../utils/preloaded-sim-upsell-step-skips";
import { persistSimCardUpsellKeepIncludedAllocation } from "../utils/sim-card-upsell-plan-persistence";
import { formatCooldownSeconds } from "../../FixedPhoneFlow/utils/activation-code-attempt-storage";
import { DeviceNotCompatibleDialog } from "./device-not-compatible-dialog";
import { usePreloadedSimDeviceVerifyFieldRateLimit } from "../hooks/use-preloaded-sim-device-verify-rate-limit";
import {
  PreloadedSimCongratulationsDialog,
  PreloadedSimNotRecognizedDialog,
  mergePreloadedSimCongratulationsDetails,
  preloadedSimDetailsFromApiResponse,
  type MobileSimInventoryBySimNumberResponse,
  type MobileSimInventoryDevice,
  type MobileSimInventoryRecord,
  type PreloadedSimCongratulationsDetails,
} from "app/pages/Customer/Activation/components/ActivationCongratulations";
import {
  getActivationFlowData,
  saveActivationFlowData,
} from "app/pages/Customer/FixedPhone/Checkout/utils/fixed-phone-storage";
import { Label } from "app/components/ui/label";
import { Input } from "app/components/ui/input";
import { Button } from "app/components/ui/button";
import { Icons } from "app/components/ui/icons";
import { toast } from "app/components/ui/use-toast";

const SKIP_PRELOADED_SIM_DEVICE_VERIFY = true;

function digitsOnlySim(value: string): string {
  return (value || "").replace(/\D/g, "");
}

function getInventoryAndDeviceFromPayload(
  payload: MobileSimInventoryBySimNumberResponse | null | undefined,
): {
  simInventory: MobileSimInventoryRecord | null;
  device: MobileSimInventoryDevice | null;
} {
  if (!payload) {
    return { simInventory: null, device: null };
  }
  const inner = payload.data ?? payload;
  const wrapped = inner as {
    simInventory?: MobileSimInventoryRecord | null;
    device?: MobileSimInventoryDevice | null;
  };
  const hasWrappedInventory = Boolean(wrapped.simInventory || wrapped.device);
  const simInventory = hasWrappedInventory
    ? wrapped.simInventory ?? null
    : (inner as MobileSimInventoryRecord);
  const device = hasWrappedInventory ? wrapped.device ?? null : null;
  return { simInventory, device };
}

function isGreenAttCompatibility(
  device: MobileSimInventoryDevice | null,
): boolean {
  const status = String(device?.attCompatibility ?? "")
    .trim()
    .toUpperCase();
  return status === "GREEN";
}

function extractDeviceCompatibilityPayload(
  payload: unknown,
): MobileSimInventoryDevice | null {
  if (!payload || typeof payload !== "object") return null;
  const obj = payload as Record<string, unknown>;
  const responseData =
    obj.data && typeof obj.data === "object"
      ? (obj.data as Record<string, unknown>)
      : obj;
  const directDeviceLike = responseData as MobileSimInventoryDevice;
  if (typeof directDeviceLike.attCompatibility === "string") {
    return directDeviceLike;
  }
  const nested = (responseData as { device?: MobileSimInventoryDevice | null })
    .device;
  if (nested && typeof nested.attCompatibility === "string") {
    return nested;
  }
  return null;
}

function mergePreloadedSimDeviceFormPatch(
  patch: Partial<{ simNumber: string; imeiNumber: string }>,
): void {
  const current = getActivationFlowData();
  saveActivationFlowData({
    preloadedSimDeviceForm: {
      ...(current?.preloadedSimDeviceForm || {}),
      ...patch,
    },
  });
}

export const DeviceInformation = () => {
  const form = useFormContext();
  const navigate = useNavigate();
  const {
    control,
    formState: { errors },
  } = form;

  const watchedSim = useWatch({ control, name: "simNumber" });
  const watchedImei = useWatch({ control, name: "imeiNumber" });
  const persistDeviceFormTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const [simVerified, setSimVerified] = useState<{ [key: number]: boolean }>({
    0: false,
  });
  const [imeiVerified, setImeiVerified] = useState<{ [key: number]: boolean }>({
    0: false,
  });

  const [preloadedCongratsOpen, setPreloadedCongratsOpen] = useState(false);
  const [simNotRecognizedOpen, setSimNotRecognizedOpen] = useState(false);
  const [deviceNotCompatibleOpen, setDeviceNotCompatibleOpen] = useState(false);
  const [preloadedCongratsDetails, setPreloadedCongratsDetails] =
    useState<PreloadedSimCongratulationsDetails>({});
  const preloadedCongratsContinueLockRef = useRef(false);

  const skipDeviceVerify = SKIP_PRELOADED_SIM_DEVICE_VERIFY;
  const simVerifyRate = usePreloadedSimDeviceVerifyFieldRateLimit(
    !skipDeviceVerify,
    "sim",
  );
  const imeiVerifyRate = usePreloadedSimDeviceVerifyFieldRateLimit(
    !skipDeviceVerify,
    "imei",
  );

  const deviceFieldsMeetDigitRules = useMemo(() => {
    const simDigits = digitsOnlySim(String(watchedSim ?? ""));
    const imeiDigits = digitsOnlySim(String(watchedImei ?? ""));
    return (
      simDigits.length >= 19 &&
      simDigits.length <= 20 &&
      imeiDigits.length === 15
    );
  }, [watchedSim, watchedImei]);
  const simDigitsLength = digitsOnlySim(String(watchedSim ?? "")).length;
  const imeiDigitsLength = digitsOnlySim(String(watchedImei ?? "")).length;
  const isSimLengthValid = simDigitsLength >= 19 && simDigitsLength <= 20;
  const isImeiLengthValid = imeiDigitsLength === 15;

  useEffect(() => {
    const zipCode = getActivationFlowData()?.zipCode;
    if (!zipCode) {
      navigate("/activation", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (skipDeviceVerify) {
      return;
    }
    const act = getActivationFlowData();
    const simDigits = digitsOnlySim(form.getValues("simNumber") || "");
    const imeiDigits = digitsOnlySim(form.getValues("imeiNumber") || "");
    const verifiedSim = (act?.preloadedSimVerifiedSimDigits || "").trim();
    const verifiedImei = (act?.preloadedSimVerifiedImei || "").trim();
    setSimVerified((prev) => ({
      ...prev,
      0: Boolean(verifiedSim && simDigits === verifiedSim),
    }));
    setImeiVerified((prev) => ({
      ...prev,
      0: Boolean(verifiedImei && imeiDigits === verifiedImei),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate once from session + default form values
  }, [skipDeviceVerify]);

  useEffect(() => {
    if (persistDeviceFormTimerRef.current) {
      clearTimeout(persistDeviceFormTimerRef.current);
    }
    persistDeviceFormTimerRef.current = setTimeout(() => {
      mergePreloadedSimDeviceFormPatch({
        simNumber: typeof watchedSim === "string" ? watchedSim : "",
        imeiNumber: typeof watchedImei === "string" ? watchedImei : "",
      });
      persistDeviceFormTimerRef.current = null;
    }, 200);
    return () => {
      if (persistDeviceFormTimerRef.current) {
        clearTimeout(persistDeviceFormTimerRef.current);
      }
    };
  }, [watchedSim, watchedImei]);

  const {
    useValidateSimMutation,
    useValidateDeviceMutation,
    useLazyGetMobileSimInventoryBySimNumberQuery,
  } = useGlobalSlice();
  const [fetchMobileSimInventory, { isFetching: isInventoryLoading }] =
    useLazyGetMobileSimInventoryBySimNumberQuery();
  const [
    validateSimMutation,
    {
      isLoading: simVerifying,
      isSuccess: simSuccess,
      isError: simError,
      error: simErrorData,
    },
  ] = useValidateSimMutation();

  const [
    validateDeviceMutation,
    {
      isLoading: imeiVerifying,
      isSuccess: imeiSuccess,
      isError: imeiError,
      error: imeiErrorData,
    },
  ] = useValidateDeviceMutation();

  useEffect(() => {
    if (simSuccess) {
      toast({
        title: "SIM Number Verified",
        description: "SIM number verified successfully!",
        variant: "success",
      });
    }
  }, [simSuccess]);

  useEffect(() => {
    if (simError) {
      toast({
        title: "SIM Verification Failed",
        description: simErrorData as string,
        variant: "error",
      });
    }
  }, [simError, simErrorData]);

  useEffect(() => {
    if (imeiSuccess) {
      toast({
        title: "IMEI Number Verified",
        description: "IMEI number verified successfully!",
        variant: "success",
      });
    }
  }, [imeiSuccess]);

  useEffect(() => {
    if (imeiError) {
      toast({
        title: "IMEI Verification Failed",
        description: imeiErrorData as string,
        variant: "error",
      });
    }
  }, [imeiError, imeiErrorData]);

  const handleOpenPreloadedCongratulations = (
    apiPayload?: MobileSimInventoryBySimNumberResponse | null,
  ) => {
    const fromApi = preloadedSimDetailsFromApiResponse(apiPayload ?? null);
    const cleanSim = (form.getValues("simNumber") || "").replace(/\s/g, "");
    const fallback: PreloadedSimCongratulationsDetails = {
      imei: form.getValues("imeiNumber") || null,
      sim: cleanSim || null,
    };
    setPreloadedCongratsDetails(
      mergePreloadedSimCongratulationsDetails(fromApi, fallback),
    );
    setPreloadedCongratsOpen(true);
  };

  const handlePreloadedCongratsContinue = useCallback(() => {
    if (preloadedCongratsContinueLockRef.current) return;
    preloadedCongratsContinueLockRef.current = true;
    try {
      const skips = resolvePreloadedSimUpsellStepSkips(
        getActivationFlowData()?.simData,
      );
      setPreloadedCongratsOpen(false);
      if (skips.skipChooseServicePlan && skips.skipSecurityPlan) {
        preparePreloadedSimSkipAllUpsellsForCheckout();
        navigate("/checkout", getPreloadedSimCheckoutNavigateState());
        return;
      }
      if (skips.skipChooseServicePlan && !skips.skipSecurityPlan) {
        persistSimCardUpsellKeepIncludedAllocation();
        navigate("/activation/preloaded-sim/security-plan-upgrade");
        return;
      }
      navigate("/activation/preloaded-sim/choose-service-plan");
    } finally {
      window.setTimeout(() => {
        preloadedCongratsContinueLockRef.current = false;
      }, 800);
    }
  }, [navigate]);

  /**
   * After SIM + IMEI verification, load inventory by query params, then show either
   * congratulations, not-recognized, or device-compatibility blocking dialog.
   */
  const handleContinueAfterDeviceVerification = async () => {
    const simNotRecognized = false;
    if (simNotRecognized) {
      setSimNotRecognizedOpen(true);
      return;
    }
    const cleanSim = (form.getValues("simNumber") || "").replace(/\s/g, "");
    if (!cleanSim) return;

    const priorActivation = getActivationFlowData();
    const priorSimData = priorActivation?.simData as
      | Record<string, unknown>
      | undefined;
    const priorCleanSim = digitsOnlySim(
      String(
        priorSimData?.simNumber ??
          priorSimData?.sim ??
          priorSimData?.sim_number ??
          "",
      ),
    ).trim();
    const nextSimDigits = digitsOnlySim(cleanSim).trim();
    const simInventoryChanged =
      Boolean(priorCleanSim) && priorCleanSim !== nextSimDigits;
    const imeiDigits = digitsOnlySim(
      String(form.getValues("imeiNumber") || ""),
    ).trim();
    const newDeviceFingerprint = buildPreloadedSimDeviceFingerprint(
      nextSimDigits,
      imeiDigits,
    );
    const priorDeviceFingerprint = (
      priorActivation?.preloadedSimDeviceCommitFingerprint || ""
    ).trim();
    const deviceIdentityChanged =
      simInventoryChanged ||
      (Boolean(priorDeviceFingerprint) &&
        priorDeviceFingerprint !== newDeviceFingerprint);

    const iccidAlignedWithSession =
      !priorCleanSim || priorCleanSim === nextSimDigits;
    const canReuseCommittedInventory =
      !deviceIdentityChanged &&
      iccidAlignedWithSession &&
      Boolean(priorDeviceFingerprint) &&
      priorDeviceFingerprint === newDeviceFingerprint &&
      Boolean(priorActivation?.simData) &&
      Boolean(priorActivation?.preloadedSimInventoryDevice);

    try {
      const response: MobileSimInventoryBySimNumberResponse =
        canReuseCommittedInventory
          ? {
              success: true as const,
              data: {
                simInventory: priorActivation?.simData,
                device: priorActivation?.preloadedSimInventoryDevice,
              },
            }
          : await fetchMobileSimInventory({
              simNumber: cleanSim,
              imeiNumber: imeiDigits,
            }).unwrap();
      if (!response?.success) {
        const failedCompatibilityDevice =
          extractDeviceCompatibilityPayload(response);
        const hasRedCompatibility =
          !isGreenAttCompatibility(failedCompatibilityDevice);
        if (failedCompatibilityDevice && hasRedCompatibility) {
          setPreloadedCongratsOpen(false);
          setSimNotRecognizedOpen(false);
          setDeviceNotCompatibleOpen(true);
          return;
        }
        setPreloadedCongratsOpen(false);
        setDeviceNotCompatibleOpen(false);
        setSimNotRecognizedOpen(true);
        return;
      }
      const { simInventory, device } =
        getInventoryAndDeviceFromPayload(response);
      if (!simInventory) {
        toast({
          title: "Unable to load SIM details",
          description: "Please try again in a moment.",
          variant: "error",
        });
        return;
      }
      if (deviceIdentityChanged) {
        clearPreloadedSimChoosePlanAndSecurityInSession();
      }
      if (!canReuseCommittedInventory) {
        saveActivationFlowData({
          simData: simInventory,
          preloadedSimInventoryDevice: device,
          preloadedSimDeviceCommitFingerprint: newDeviceFingerprint,
        });
      }
      mergePreloadedSimDeviceFormPatch({
        simNumber: form.getValues("simNumber") || "",
        imeiNumber: form.getValues("imeiNumber") || "",
      });
      if (!hasPreloadedSimIncludedPlan(simInventory)) {
        setPreloadedCongratsOpen(false);
        setDeviceNotCompatibleOpen(false);
        setSimNotRecognizedOpen(true);
        return;
      }
      if (!isGreenAttCompatibility(device)) {
        setPreloadedCongratsOpen(false);
        setSimNotRecognizedOpen(false);
        setDeviceNotCompatibleOpen(true);
        return;
      }
      handleOpenPreloadedCongratulations(response);
    } catch (error: unknown) {
      const err = error as {
        data?: MobileSimInventoryBySimNumberResponse | { message?: string };
        message?: string;
      };
      const failedCompatibilityDevice = extractDeviceCompatibilityPayload(
        err?.data ?? null,
      );
      const hasRedCompatibility = !isGreenAttCompatibility(
        failedCompatibilityDevice,
      );
      if (failedCompatibilityDevice && hasRedCompatibility) {
        setPreloadedCongratsOpen(false);
        setSimNotRecognizedOpen(false);
        setDeviceNotCompatibleOpen(true);
        return;
      }
      const responseLike = err?.data as
        | MobileSimInventoryBySimNumberResponse
        | undefined;
      if (
        responseLike &&
        typeof responseLike === "object" &&
        responseLike.success === false
      ) {
        setPreloadedCongratsOpen(false);
        setDeviceNotCompatibleOpen(false);
        setSimNotRecognizedOpen(true);
        return;
      }
      const description =
        (typeof err?.data?.message === "string" && err.data.message) ||
        (typeof err?.message === "string" && err.message) ||
        "We could not load your SIM inventory. Please try again.";
      toast({
        title: "SIM inventory",
        description,
        variant: "error",
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey) return;
    if (
      [
        "Backspace",
        "Delete",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End",
      ].includes(e.key)
    )
      return;
    if (!/\d/.test(e.key)) e.preventDefault();
  };

  const handleSimValidation = async () => {
    const simNumber = form.getValues("simNumber");
    if (!simNumber || simVerifyRate.isBlocked) return;

    try {
      const cleanSimNumber = simNumber.replace(/\s/g, "");
      await validateSimMutation({
        sim: cleanSimNumber,
      }).unwrap();
      setSimVerified((prev) => ({ ...prev, 0: true }));
      form.clearErrors("simNumber");
      saveActivationFlowData({
        preloadedSimVerifiedSimDigits: cleanSimNumber,
      });
      mergePreloadedSimDeviceFormPatch({
        simNumber: form.getValues("simNumber") || "",
        imeiNumber: form.getValues("imeiNumber") || "",
      });
    } catch (error: unknown) {
      setSimVerified((prev) => ({ ...prev, 0: false }));
      console.error("SIM validation error:", error);
    } finally {
      simVerifyRate.registerAttempt();
    }
  };

  const handleImeiValidation = async () => {
    const imeiNumber = form.getValues("imeiNumber");
    if (!imeiNumber || imeiVerifyRate.isBlocked) return;

    try {
      await validateDeviceMutation({
        imei: imeiNumber,
      }).unwrap();
      setImeiVerified((prev) => ({ ...prev, 0: true }));
      form.clearErrors("imeiNumber");
      saveActivationFlowData({
        preloadedSimVerifiedImei: String(imeiNumber).replace(/\D/g, ""),
      });
      mergePreloadedSimDeviceFormPatch({
        simNumber: form.getValues("simNumber") || "",
        imeiNumber: form.getValues("imeiNumber") || "",
      });
    } catch (error: unknown) {
      setImeiVerified((prev) => ({ ...prev, 0: false }));
      console.error("IMEI validation error:", error);
    } finally {
      imeiVerifyRate.registerAttempt();
    }
  };

  const isSimVerified = simVerified[0] || false;
  const isImeiVerified = imeiVerified[0] || false;

  const areInputsVerified = skipDeviceVerify
    ? deviceFieldsMeetDigitRules
    : isSimVerified && isImeiVerified;

  const onSubmit = async () => {
    if (!areInputsVerified || isInventoryLoading) {
      return;
    }
    await handleContinueAfterDeviceVerification();
  };

  return (
    <React.Fragment>
      <form
        className="w-full max-w-[32rem] mx-auto space-y-4 sm:space-y-5 lg:space-y-6"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className="bg-white w-full p-4 sm:p-5 lg:p-6 border border-[#E5E7EB] rounded-xl md:rounded-2xl shadow-sm">
          <h4 className="text-[1.375rem] font-bold leading-5 text-black sm:text-[1.35rem] lg:text-2xl sm:leading-6 lg:leading-7 font-mobile mb-[1.125rem]">
            Enter your device information
          </h4>
          <div className="w-full aspect-[224/141] rounded-[1.15rem] sm:rounded-[1.25rem] lg:rounded-[1.375rem] overflow-hidden mb-5 sm:mb-6 lg:mb-[1.875rem]">
            <img
              src="/assets/sim-card-5G-image.webp"
              className="object-cover size-full"
              alt="Sim Card"
            />
          </div>
          <div className="space-y-4 sm:space-y-[1.125rem] lg:space-y-5">
            <div className="">
              <Label
                htmlFor="simNumber"
                className="w-fit text-black text-[0.8125rem] font-bold leading-4 md:leading-5 mb-[0.5625rem] md:mb-[0.625rem]"
              >
                Enter the SIM/eSIM number provided
              </Label>
              <div className="relative">
                <Controller
                  name="simNumber"
                  control={control}
                  rules={{
                    required: "SIM number is required!",
                    validate: {
                      validLength: (value) => {
                        if (!value) return true;
                        const iccid = value.replace(/\D/g, "");
                        if (iccid.length < 19 || iccid.length > 20) {
                          return "SIM number must be 19 or 20 digits";
                        }
                        return true;
                      },
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      id="simNumber"
                      type="tel"
                      inputMode="numeric"
                      maxLength={24}
                      value={field.value || ""}
                      onChange={(e) => {
                        const numbers = e.target.value.replace(/\D/g, "");
                        const formatted = numbers.replace(
                          /(\d{4})(?=\d)/g,
                          "$1 ",
                        );
                        const finalValue = formatted.slice(0, 24);

                        field.onChange(finalValue);

                        const numericValue = numbers;
                        if (
                          numericValue.length >= 19 &&
                          numericValue.length <= 20
                        ) {
                          form.clearErrors("simNumber");
                        } else {
                          setTimeout(() => form.trigger("simNumber"), 0);
                        }
                        const act = getActivationFlowData();
                        const verifiedSim = (
                          act?.preloadedSimVerifiedSimDigits || ""
                        ).trim();
                        if (verifiedSim && numericValue !== verifiedSim) {
                          saveActivationFlowData({
                            preloadedSimVerifiedSimDigits: undefined,
                            preloadedSimVerifiedImei: undefined,
                          });
                        }
                        setSimVerified((prev) => ({ ...prev, 0: false }));
                        maybeResetPreloadedSimAfterCommittedDeviceEdited(
                          numericValue,
                          digitsOnlySim(
                            String(form.getValues("imeiNumber") || ""),
                          ),
                        );
                      }}
                      onKeyDown={handleKeyDown}
                      className={`bg-white h-[2.5rem] md:h-[2.875rem] p-2 md:p-[0.78125rem] md:pb-[0.84375rem] rounded-xl text-black placeholder:text-[#9CA3AF] text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline ${
                        errors.simNumber
                          ? "border-[#D80027] focus:outline-[#D80027]"
                          : "border-[#D1D5DB] focus:outline-[#707C8F]"
                      } ${
                        skipDeviceVerify ? "pr-2 md:pr-[0.78125rem]" : "pr-20"
                      }`}
                      disabled={!skipDeviceVerify && simVerifyRate.isBlocked}
                      placeholder="ICCID (19-20 digits)"
                    />
                  )}
                />
                {!skipDeviceVerify ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className={`absolute right-3 md:right-3.5 top-1/2 -translate-y-1/2 bg-transparent hover:bg-transparent text-[#FE4D01] hover:text-[#E44501] text-[0.72rem] sm:text-[0.8125rem] font-bold leading-4 sm:leading-5 px-0 ${
                      isSimVerified ? "disabled:opacity-100" : ""
                    }`}
                    onClick={handleSimValidation}
                    disabled={
                      simVerifying ||
                      isSimVerified ||
                      !isSimLengthValid ||
                      (!skipDeviceVerify && simVerifyRate.isBlocked)
                    }
                  >
                    {simVerifying ? (
                      <div className="flex items-center gap-0.5">
                        <span>Verifying</span>
                        <Icons.Spinner className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
                      </div>
                    ) : isSimVerified ? (
                      <img
                        src="/assets/tick-circle.svg"
                        alt="check"
                        className="size-4 sm:size-[1.125rem] lg:size-5"
                      />
                    ) : (
                      "Verify"
                    )}
                  </Button>
                ) : null}
              </div>
              {errors.simNumber && (
                <p className="text-[#D80027] text-[0.72rem] sm:text-xs font-normal leading-5 mt-0.5 sm:mt-1">
                  <span className="font-bold">*</span>
                  {errors.simNumber?.message as string}
                </p>
              )}
              {!skipDeviceVerify && simVerifyRate.inCooldown ? (
                <p
                  className="text-xs sm:text-[0.8125rem] font-medium leading-4 sm:leading-[1.125rem] mt-0.5 sm:mt-1 text-[#991B1B]"
                  role="status"
                  aria-live="polite"
                >
                  Please wait{" "}
                  <span className="tabular-nums font-semibold">
                    {formatCooldownSeconds(simVerifyRate.remainingSeconds)}
                  </span>{" "}
                  before trying again.
                </p>
              ) : null}
            </div>
            <div className="">
              <Label
                htmlFor="imeiNumber"
                className="w-fit text-black text-[0.8125rem] font-bold leading-4 md:leading-5 mb-[0.5625rem] md:mb-[0.625rem]"
              >
                Enter IMEI Number
              </Label>
              <div className="relative">
                <Controller
                  name="imeiNumber"
                  control={control}
                  rules={{
                    required: "IMEI number is required!",
                    validate: {
                      exactLength: (value) => {
                        if (!value) return true;
                        const imei = value.replace(/\D/g, "");
                        if (imei.length !== 15) {
                          return "IMEI number must be exactly 15 digits";
                        }
                        return true;
                      },
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      id="imeiNumber"
                      type="tel"
                      inputMode="numeric"
                      maxLength={15}
                      value={field.value || ""}
                      onChange={(e) => {
                        const numbers = e.target.value.replace(/\D/g, "");
                        const finalValue = numbers.slice(0, 15);

                        field.onChange(finalValue);

                        if (numbers.length === 15) {
                          form.clearErrors("imeiNumber");
                        } else {
                          setTimeout(() => form.trigger("imeiNumber"), 0);
                        }
                        const act = getActivationFlowData();
                        const verifiedImei = (
                          act?.preloadedSimVerifiedImei || ""
                        ).trim();
                        if (verifiedImei && numbers !== verifiedImei) {
                          saveActivationFlowData({
                            preloadedSimVerifiedImei: undefined,
                          });
                        }
                        setImeiVerified((prev) => ({ ...prev, 0: false }));
                        maybeResetPreloadedSimAfterCommittedDeviceEdited(
                          digitsOnlySim(
                            String(form.getValues("simNumber") || ""),
                          ),
                          numbers,
                        );
                      }}
                      onKeyDown={handleKeyDown}
                      className={`bg-white h-[2.5rem] md:h-[2.875rem] p-2 md:p-[0.78125rem] md:pb-[0.84375rem] rounded-xl text-black placeholder:text-[#9CA3AF] text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline ${
                        errors.imeiNumber
                          ? "border-[#D80027] focus:outline-[#D80027]"
                          : "border-[#D1D5DB] focus:outline-[#707C8F]"
                      } ${
                        skipDeviceVerify ? "pr-2 md:pr-[0.78125rem]" : "pr-20"
                      }`}
                      disabled={!skipDeviceVerify && imeiVerifyRate.isBlocked}
                      placeholder="IMEI (15 digits)"
                    />
                  )}
                />
                {!skipDeviceVerify ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className={`absolute right-3 md:right-3.5 top-1/2 -translate-y-1/2 bg-transparent hover:bg-transparent text-[#FE4D01] hover:text-[#E44501] text-[0.72rem] sm:text-[0.8125rem] font-bold leading-4 sm:leading-5 px-0 ${
                      isImeiVerified ? "disabled:opacity-100" : ""
                    }`}
                    onClick={handleImeiValidation}
                    disabled={
                      imeiVerifying ||
                      isImeiVerified ||
                      !isImeiLengthValid ||
                      (!skipDeviceVerify && imeiVerifyRate.isBlocked)
                    }
                  >
                    {imeiVerifying ? (
                      <div className="flex items-center gap-0.5">
                        <span>Verifying</span>
                        <Icons.Spinner className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
                      </div>
                    ) : isImeiVerified ? (
                      <img
                        src="/assets/tick-circle.svg"
                        alt="check"
                        className="size-4 sm:size-[1.125rem] lg:size-5"
                      />
                    ) : (
                      "Verify"
                    )}
                  </Button>
                ) : null}
              </div>
              {errors.imeiNumber && (
                <p className="text-[#D80027] text-[0.72rem] sm:text-xs font-normal leading-5 mt-0.5 sm:mt-1">
                  <span className="font-bold">*</span>
                  {errors.imeiNumber?.message as string}
                </p>
              )}
              {!skipDeviceVerify && imeiVerifyRate.inCooldown ? (
                <p
                  className="text-xs sm:text-[0.8125rem] font-medium leading-4 sm:leading-[1.125rem] mt-0.5 sm:mt-1 text-[#991B1B]"
                  role="status"
                  aria-live="polite"
                >
                  Please wait{" "}
                  <span className="tabular-nums font-semibold">
                    {formatCooldownSeconds(imeiVerifyRate.remainingSeconds)}
                  </span>{" "}
                  before trying again.
                </p>
              ) : null}
            </div>
          </div>
        </div>
        <Button
          type="submit"
          variant="primary"
          disabled={
            !areInputsVerified ||
            isInventoryLoading ||
            (!skipDeviceVerify &&
              ((simVerifyRate.isBlocked && !isSimVerified) ||
                (imeiVerifyRate.isBlocked && !isImeiVerified)))
          }
        >
          {isInventoryLoading ? (
            <div className="flex items-center gap-0.5 sm:gap-1">
              <Icons.Spinner className="w-4 h-4 animate-spin md:w-5 md:h-5" />
              <span>Loading...</span>
            </div>
          ) : (
            "Continue"
          )}
        </Button>
      </form>
      <PreloadedSimNotRecognizedDialog
        showDialog={simNotRecognizedOpen}
        setShowDialog={setSimNotRecognizedOpen}
      />
      <PreloadedSimCongratulationsDialog
        showDialog={preloadedCongratsOpen}
        setShowDialog={setPreloadedCongratsOpen}
        details={preloadedCongratsDetails}
        onContinue={handlePreloadedCongratsContinue}
      />
      <DeviceNotCompatibleDialog
        showDialog={deviceNotCompatibleOpen}
        setShowDialog={setDeviceNotCompatibleOpen}
        imeiNumber={form.getValues("imeiNumber") || null}
        simNumber={
          (form.getValues("simNumber") || "").replace(/\s/g, "") || null
        }
      />
    </React.Fragment>
  );
};
